// ============================================================
// FEAT-02: Controle de Amamentação com regras automáticas
// Substituir em /opt/ovinogest/frontend/src/pages/Amamentacao.js
//
// Fases automáticas:
//   0–20 dias:  Amamentação Livre
//   20–75 dias: Mamada Controlada
//   60+ dias:   Alerta de Desmame
//   Desmamado:  Registro manual ou automático
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { Baby, AlertTriangle, CheckCircle, Clock, Filter, Search, Download, ArrowRightCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';

// ── Fases ──
// A FASE reflete o MANEJO registrado (amamentacao.status), não a idade:
//   status 'livre'      -> Amamentação Livre  (até o produtor registrar a transferência)
//   status 'controlada' -> Amamentação Controlada
//   status 'desmamado'  -> Desmamado
// "desmame" é apenas um estado de ALERTA (idade > 60 d e ainda não desmamado).
const FASES = {
  livre:      { label: 'Amamentação Livre',      cor: '#22c55e', bg: '#dcfce7' },
  controlada: { label: 'Amamentação Controlada', cor: '#f59e0b', bg: '#fef3c7' },
  desmame:    { label: 'Alerta de Desmame',      cor: '#ef4444', bg: '#fee2e2' },
  desmamado:  { label: 'Desmamado',              cor: '#6b7280', bg: '#f3f4f6' },
};

const TRANSICAO_IDADE_MIN = 30; // 30 dias completos p/ iniciar Amamentação Controlada
const DESMAME_IDADE_MIN = 60;   // idade > 60
const DESMAME_PESO_MIN = 15;    // peso  > 15 kg

function calcularFase(idadeDias, status) {
  if (status === 'desmamado') return 'desmamado';
  if (status === 'controlada') return 'controlada';
  if (idadeDias != null && idadeDias > 60) return 'desmame';
  return 'livre';
}

const Amamentacao = () => {
  const [cordeiros, setCordeiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroFase, setFiltroFase] = useState('');
  const [busca, setBusca] = useState('');
  const [desmameModal, setDesmameModal] = useState(null); // cordeiro selecionado
  const [pesoDesmame, setPesoDesmame] = useState('');
  const [salvandoDesmame, setSalvandoDesmame] = useState(false);
  const [transferModal, setTransferModal] = useState(null); // cordeiro p/ iniciar controlada
  const [salvandoTransfer, setSalvandoTransfer] = useState(false);

  const carregarCordeiros = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/amamentacao');
      const comFase = data.map(c => {
        const idadeDias = c.idade_dias != null ? Number(c.idade_dias) : null;
        const fase = calcularFase(idadeDias, c.status);
        return { ...c, idade_dias: idadeDias, fase };
      });
      setCordeiros(comFase);
    } catch (err) {
      toast.error('Erro ao carregar dados de amamentação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarCordeiros();
  }, [carregarCordeiros]);

  // ── Desmamar cordeiro (exige idade > 60 d E peso > 15 kg) ──
  const abrirDesmame = (cordeiro) => { setPesoDesmame(''); setDesmameModal(cordeiro); };

  const pesoNum = parseFloat(pesoDesmame);
  const desmameIdadeOk = !!desmameModal && desmameModal.idade_dias != null && desmameModal.idade_dias > DESMAME_IDADE_MIN;
  const desmamePesoOk = !isNaN(pesoNum) && pesoNum > DESMAME_PESO_MIN;

  const confirmarDesmame = async () => {
    if (!desmameModal) return;
    if (!desmameIdadeOk || !desmamePesoOk) return;
    setSalvandoDesmame(true);
    try {
      const { data } = await api.put(`/amamentacao/${desmameModal.id}`, {
        status: 'desmamado',
        data_desmame: todayLocalISO(),
        peso_desmame: pesoNum,
      });
      toast.success(data.message || 'Desmame registrado · pesagem de desmame criada');
      setDesmameModal(null);
      setPesoDesmame('');
      carregarCordeiros();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registrar desmame');
    } finally {
      setSalvandoDesmame(false);
    }
  };

  // ── Transferência de manejo: Livre -> Controlada (evento explícito do produtor) ──
  const precisaTransferir = (c) => c && c.status === 'livre' && c.idade_dias != null && c.idade_dias >= TRANSICAO_IDADE_MIN;
  const podeDesmamar = (c) => c && c.status !== 'desmamado' && c.idade_dias != null && c.idade_dias > DESMAME_IDADE_MIN;

  const confirmarTransfer = async () => {
    if (!transferModal) return;
    setSalvandoTransfer(true);
    try {
      const { data } = await api.put(`/amamentacao/${transferModal.id}`, { status: 'controlada' });
      toast.success(data.message || 'Transferido para Amamentação Controlada');
      setTransferModal(null);
      carregarCordeiros();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao transferir');
    } finally {
      setSalvandoTransfer(false);
    }
  };

  // ── Filtros ──
  const cordeirosFiltrados = cordeiros.filter(c => {
    if (filtroFase === 'desmame') { if (!podeDesmamar(c)) return false; }
    else if (filtroFase) { if (c.fase !== filtroFase) return false; }
    // Sem card selecionado: lista de trabalho padrão não mostra quem já foi desmamado
    // (esses só aparecem clicando no card "Desmamado").
    else if (c.status === 'desmamado') return false;
    if (busca) {
      const termo = busca.toLowerCase();
      const brinco = (c.brinco || '').toLowerCase();
      const nome = (c.animal_nome || '').toLowerCase();
      if (!brinco.includes(termo) && !nome.includes(termo)) return false;
    }
    return true;
  });

  // ── Resumo por fase (status de manejo registrado — não a idade atual; ver calcularFase) ──
  const resumo = {
    livre: cordeiros.filter(c => c.status === 'livre' && !(c.idade_dias > 60)).length,
    controlada: cordeiros.filter(c => c.status === 'controlada').length,
    desmame: cordeiros.filter(c => c.status !== 'desmamado' && c.idade_dias > 60).length,
    desmamado: cordeiros.filter(c => c.status === 'desmamado').length,
  };

  // ── Exportar CSV ──
  const exportarCSV = () => {
    if (cordeirosFiltrados.length === 0) return toast.error('Nenhum dado');
    const headers = ['Brinco', 'Nome', 'Idade (dias)', 'Fase', 'Mãe', 'Status'];
    const rows = cordeirosFiltrados.map(c => [
      c.brinco || '', c.animal_nome || '', c.idade_dias || '', FASES[c.fase]?.label || '',
      c.mae_brinco || '', c.status || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `amamentacao_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <Baby size={24} />
          <h1>Controle de Amamentação</h1>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportarCSV}>
            <Download size={16} /> CSV
          </button>
        </div>
      </div>

      {/* Cards de resumo por fase */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {Object.entries(FASES).map(([key, fase]) => (
          <div
            key={key}
            className="card"
            onClick={() => setFiltroFase(filtroFase === key ? '' : key)}
            style={{
              padding: '16px',
              cursor: 'pointer',
              border: filtroFase === key ? `2px solid ${fase.cor}` : '1px solid #e5e7eb',
              backgroundColor: fase.bg,
              textAlign: 'center',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: fase.cor }}>
              {resumo[key]}
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: fase.cor, marginTop: '4px' }}>
              {fase.label}
            </div>
            {key === 'livre' && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>0–30 dias</div>}
            {key === 'desmame' && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>&gt; 60 dias</div>}
          </div>
        ))}
      </div>

      {/* Busca */}
      <div style={{ marginBottom: '16px', position: 'relative' }}>
        <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input
          type="text"
          className="input"
          placeholder="Buscar por brinco ou nome..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ paddingLeft: '34px', width: '100%', maxWidth: '400px' }}
        />
      </div>

      {/* Lista de cordeiros */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      ) : cordeirosFiltrados.length === 0 ? (
        <div className="empty-state">
          <Baby size={48} />
          <h3>Nenhum cordeiro encontrado</h3>
          <p>Os cordeiros aparecem automaticamente após o registro de parto.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {cordeirosFiltrados.map(c => {
            const faseInfo = FASES[c.fase] || FASES.livre;
            return (
              <div
                key={c.id}
                className="card"
                style={{
                  padding: '14px 18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderLeft: `4px solid ${faseInfo.cor}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                  <div>
                    <strong style={{ fontSize: '15px' }}>{c.brinco || c.animal_nome}</strong>
                    {c.mae_brinco && (
                      <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                        Mãe: {c.mae_brinco}
                      </span>
                    )}
                  </div>

                  <span
                    style={{
                      backgroundColor: faseInfo.bg,
                      color: faseInfo.cor,
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {faseInfo.label}
                  </span>

                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    <Clock size={13} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
                    {c.idade_dias != null ? `${c.idade_dias} dias` : 'Idade desconhecida'}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {precisaTransferir(c) && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '4px 8px', borderRadius: '6px',
                      backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '12px', fontWeight: 'bold',
                    }} title="Cordeiro completou 30 dias: registrar a transferência para Amamentação Controlada. Encerra-se o período de Amamentação Livre.">
                      <AlertTriangle size={14} /> TRANSFERIR P/ CONTROLADA
                    </div>
                  )}

                  {podeDesmamar(c) && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '4px 8px', borderRadius: '6px',
                      backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '12px', fontWeight: 'bold',
                      animation: 'pulse 2s infinite',
                    }}>
                      <AlertTriangle size={14} /> DESMAME
                    </div>
                  )}

                  {precisaTransferir(c) && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => setTransferModal(c)}
                      style={{ fontSize: '13px', padding: '6px 14px', color: '#0369a1', borderColor: '#0369a1' }}
                    >
                      <ArrowRightCircle size={14} /> Iniciar Amamentação Controlada
                    </button>
                  )}

                  {podeDesmamar(c) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => abrirDesmame(c)}
                      style={{ fontSize: '13px', padding: '6px 14px' }}
                    >
                      <CheckCircle size={14} /> Desmamar
                    </button>
                  )}

                  {c.status === 'desmamado' && (
                    <span style={{ color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
                      Desmamado {c.data_desmame ? `em ${formatDateBR(c.data_desmame)}` : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legenda */}
      <div style={{
        marginTop: '24px', padding: '16px',
        backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb'
      }}>
        <strong style={{ fontSize: '14px' }}>Como funciona o Controle de Amamentação:</strong>
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#374151', lineHeight: '1.8' }}>
          <p><span style={{ color: FASES.livre.cor, fontWeight: 'bold' }}>● Amamentação Livre:</span> O cordeiro fica junto à mãe em tempo integral. Aos <strong>30 dias completos</strong> aparece o alerta e o botão <em>Iniciar Amamentação Controlada</em> — a fase só muda quando o produtor <strong>confirma</strong> essa ação (não é automático pela idade).</p>
          <p><span style={{ color: FASES.controlada.cor, fontWeight: 'bold' }}>● Amamentação Controlada:</span> registrada após a confirmação da transferência. O cordeiro é separado e mama em horários controlados até o desmame. A transferência fica no histórico do animal.</p>
          <p><span style={{ color: FASES.desmame.cor, fontWeight: 'bold' }}>● Alerta de Desmame (&gt; 60 dias):</span> Só é possível desmamar com idade &gt; 60 dias <strong>e</strong> peso &gt; 15 kg. O peso informado no desmame entra no histórico de pesagens.</p>
          <p><span style={{ color: FASES.desmamado.cor, fontWeight: 'bold' }}>● Desmamado:</span> Desmame registrado. A categoria do animal é atualizada automaticamente.</p>
        </div>
      </div>

      {/* Modal de desmame */}
      {desmameModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDesmameModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Desmame — {desmameModal.brinco || desmameModal.animal_nome}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setDesmameModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                Idade: <strong style={{ color: desmameIdadeOk ? '#16a34a' : '#dc2626' }}>
                  {desmameModal.idade_dias != null ? `${desmameModal.idade_dias} dias` : 'desconhecida'}
                </strong> {desmameIdadeOk ? '' : '(exige > 60 dias)'}
              </div>
              <div className="form-group">
                <label>Peso no momento do desmame (kg) *</label>
                <input type="number" step="0.1" min="0" value={pesoDesmame}
                  onChange={e => setPesoDesmame(e.target.value)} placeholder="Ex: 16.5" autoFocus />
              </div>
              {pesoDesmame !== '' && !desmamePesoOk && (
                <div style={{ fontSize: 12, color: '#dc2626' }}>O peso precisa ser maior que 15 kg.</div>
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                O peso será registrado como uma pesagem oficial (histórico de pesagens).
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDesmameModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarDesmame}
                disabled={salvandoDesmame || !desmameIdadeOk || !desmamePesoOk}>
                {salvandoDesmame ? 'Registrando...' : 'Confirmar Desmame'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: iniciar Amamentação Controlada */}
      {transferModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setTransferModal(null)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Iniciar Amamentação Controlada?</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setTransferModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14 }}>
                <strong>{transferModal.brinco || transferModal.animal_nome}</strong> completou{' '}
                <strong>{transferModal.idade_dias} dias</strong> e está apto a iniciar o período de
                Amamentação Controlada.
              </p>
              <p style={{ fontSize: 13, color: '#6b7280' }}>
                Fase anterior: Amamentação Livre → Nova fase: Amamentação Controlada.
                A transferência será registrada no histórico do animal (data e responsável).
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTransferModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarTransfer} disabled={salvandoTransfer}>
                {salvandoTransfer ? 'Registrando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Amamentacao;
